import { useCallback, useRef } from "react";
import type { UserIntentProfileData } from "@/hooks/useUserIntentProfile";

export interface GuidedAction {
  label: string;
  icon: string;
  key: string;
}

export interface GuidedStep {
  content: string;
  actions: GuidedAction[];
}

const GUIDED_KEY = "brain-guided-done";

export function isGuidedDone(): boolean {
  try {
    return localStorage.getItem(GUIDED_KEY) === "1";
  } catch {
    return false;
  }
}

export function markGuidedDone() {
  try {
    localStorage.setItem(GUIDED_KEY, "1");
  } catch {}
}

function buildStep1(): GuidedStep {
  return {
    content:
      "👋\nJe suis là pour t'aider à prendre en main le site et à créer ton premier programme.\n\nJe peux te guider pas à pas, ou rester en retrait si tu préfères explorer seul.\nTu peux aussi me poser des questions maintenant ou plus tard, quand tu veux.",
    actions: [
      { label: "Créer mon premier programme", icon: "👉", key: "create_program" },
      { label: "Explorer le site", icon: "👀", key: "explore" },
      { label: "Poser une question", icon: "❓", key: "ask" },
      { label: "Plus tard", icon: "⏭️", key: "later" },
    ],
  };
}

function buildStep2(): GuidedStep {
  return {
    content:
      "Parfait.\nJe vais t'aider à construire un programme cohérent à partir de ce que je sais déjà de toi.\n\nÀ chaque étape, je t'explique le pourquoi.\nRien n'est appliqué sans ton accord.",
    actions: [
      { label: "Commencer", icon: "▶️", key: "start_build" },
      { label: "Poser une question", icon: "❓", key: "ask" },
      { label: "Annuler", icon: "⏭️", key: "cancel" },
    ],
  };
}

function buildStep3(profile: UserIntentProfileData | null): GuidedStep {
  const obj = profile?.objectif_principal || "non défini";
  const jours = profile?.jours_par_semaine || "?";
  const duree = profile?.duree_par_session || "?";

  let content = `D'après ton profil, ton objectif principal est **${obj}**, avec **${jours} jours/semaine** et **${duree} min** par séance.\n\nJe vais m'appuyer là-dessus pour te proposer quelque chose de logique.`;

  // Simple coherence check
  const joursNum = typeof jours === "number" ? jours : 0;
  const dureeNum = typeof duree === "number" ? duree : 0;
  if (joursNum >= 6 && dureeNum >= 90) {
    content +=
      "\n\n⚠️ Je vois un volume élevé entre ta fréquence et la durée de tes séances. On pourra ajuster ensemble si tu veux.";
  }

  return {
    content,
    actions: [
      { label: "Continuer comme ça", icon: "✔️", key: "continue_profile" },
      { label: "Ajuster", icon: "🔧", key: "adjust_profile" },
      { label: "Pourquoi ?", icon: "❓", key: "explain_profile" },
    ],
  };
}

function buildStep4(): GuidedStep {
  return {
    content:
      "Un programme efficace repose surtout sur :\n\n• une fréquence réaliste\n• une charge bien répartie\n• du repos au bon moment\n\nOn va commencer par la structure générale.",
    actions: [
      { label: "Voir la structure proposée", icon: "🧩", key: "show_structure" },
      { label: "Expliquer autrement", icon: "❓", key: "explain_structure" },
      { label: "Passer", icon: "⏭️", key: "skip_structure" },
    ],
  };
}

function buildStep5(): GuidedStep {
  return {
    content:
      "Voilà ce que je te propose comme base.\n👉 Ce n'est pas définitif.\nTu peux modifier, poser des questions ou refuser.",
    actions: [
      { label: "Ça me va", icon: "👍", key: "accept_proposal" },
      { label: "Ajuster", icon: "🔄", key: "adjust_proposal" },
      { label: "Pourquoi ce choix ?", icon: "❓", key: "explain_proposal" },
      { label: "Modifier manuellement", icon: "✏️", key: "manual_edit" },
    ],
  };
}

function buildStep6(): GuidedStep {
  return {
    content:
      "Avant d'appliquer quoi que ce soit :\nveux-tu valider cette version, ou continuer à l'ajuster ?",
    actions: [
      { label: "Valider", icon: "✅", key: "validate" },
      { label: "Continuer à ajuster", icon: "🔁", key: "keep_adjusting" },
      { label: "Passer en manuel", icon: "🛠️", key: "go_manual" },
    ],
  };
}

function buildStep7(): GuidedStep {
  return {
    content:
      "Ton programme est en place.\nTu peux maintenant :\n\n• me demander d'expliquer un choix\n• ajuster une semaine\n• ou simplement suivre le planning",
    actions: [
      { label: "Me poser une question", icon: "🧠", key: "ask_final" },
      { label: "Ajuster plus tard", icon: "🔧", key: "adjust_later" },
      { label: "C'est bon", icon: "👍", key: "done" },
    ],
  };
}

function buildStep8(): GuidedStep {
  return {
    content:
      "Petit rappel :\ntu peux revenir me parler à n'importe quel moment.\nPour comprendre, ajuster ou corriger — même après le tuto.",
    actions: [],
  };
}

const STEP_BUILDERS: Record<number, (profile: UserIntentProfileData | null) => GuidedStep> = {
  1: () => buildStep1(),
  2: () => buildStep2(),
  3: (p) => buildStep3(p),
  4: () => buildStep4(),
  5: () => buildStep5(),
  6: () => buildStep6(),
  7: () => buildStep7(),
  8: () => buildStep8(),
};

// Maps action keys to what step comes next (or special behavior)
const ACTION_NEXT: Record<string, number | "navigate" | "ask" | "done"> = {
  // Step 1
  create_program: 2,
  explore: "done",
  ask: "ask",
  later: "done",
  // Step 2
  start_build: 3,
  cancel: "done",
  // Step 3
  continue_profile: 4,
  adjust_profile: "navigate", // → /profile
  explain_profile: "ask",
  // Step 4
  show_structure: 5,
  explain_structure: "ask",
  skip_structure: 5,
  // Step 5
  accept_proposal: 6,
  adjust_proposal: "ask",
  explain_proposal: "ask",
  manual_edit: "navigate", // → /programs
  // Step 6
  validate: 7,
  keep_adjusting: "ask",
  go_manual: "navigate",
  // Step 7
  ask_final: "ask",
  adjust_later: 8,
  done: 8,
};

export interface GuidedFlowController {
  getStep: (stepNum: number) => GuidedStep;
  getNextStep: (actionKey: string) => { type: "step"; step: number } | { type: "ask" } | { type: "done" } | { type: "navigate"; route: string };
}

export function useGuidedFlow(profile: UserIntentProfileData | null): GuidedFlowController {
  const profileRef = useRef(profile);
  profileRef.current = profile;

  const getStep = useCallback(
    (stepNum: number): GuidedStep => {
      const builder = STEP_BUILDERS[stepNum];
      if (!builder) return buildStep8();
      return builder(profileRef.current);
    },
    []
  );

  const getNextStep = useCallback(
    (actionKey: string) => {
      const next = ACTION_NEXT[actionKey];
      if (next === undefined || next === "done") return { type: "done" as const };
      if (next === "ask") return { type: "ask" as const };
      if (next === "navigate") {
        // Determine route based on action
        if (actionKey === "adjust_profile") return { type: "navigate" as const, route: "/profile" };
        if (actionKey === "manual_edit" || actionKey === "go_manual") return { type: "navigate" as const, route: "/programs" };
        return { type: "navigate" as const, route: "/dashboard" };
      }
      return { type: "step" as const, step: next };
    },
    []
  );

  return { getStep, getNextStep };
}
